import { Node } from '@tiptap/core'
import { DOMSerializer } from '@tiptap/pm/model'
import DOMPurify from 'dompurify'
import { closeHistory } from '@tiptap/pm/history'
import { chartBlockSpec, chartFonts, chartReference, forgetChartWidths } from '../charts/chartBlock.js'
import { endSessionOf, observable, registerChartView, startChartEdit, unregisterChartView } from '../charts/chartEditing.js'
import { fontShorthand } from '../charts/chartText.js'
import { drawingAnchorInfo, emuToPx, inlinePictureCss, rPrToCss, rStyleOf, runToggleOffClasses, textBoxCss } from '../ooxmlCss.js'
import {
  flowAnchorMarginLeftPx,
  flowAnchorMarginTopPx,
  flowAnchorWidthPx,
  isPageProjected,
} from '../layout/anchorPlacement.js'
import { mmToPx } from '../layout/paginator.js'
import { footnoteText, mediaUrl, objectFonts, objectPreview, objectSheet, pageSection, presentationPayload, registryEntry, studioLabel } from '../studioContext.js'
import { patchPreview } from '../objects/objectPreview.js'

/**
 * ${code} placeholder pill. Pure renderHTML — templates carry hundreds of
 * these, so no Vue node view (the M3+ perf keystone). Label/validity are
 * derived from the registry at render time and never stored.
 */
export const TemplateCode = Node.create({
  name: 'templateCode',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      code: { default: '' },
    }
  },

  renderHTML({ node }) {
    const entry = registryEntry(node.attrs.code)
    // Label AND sample ride as data attrs; CSS ::before picks one based on
    // the canvas mode (.is-filled-preview) — the filled toggle is zero JS.
    return [
      'span',
      {
        class: entry ? 'studio-code-pill' : 'studio-code-pill is-invalid',
        'data-code': node.attrs.code,
        'data-label': entry ? entry.labelEt : node.attrs.code,
        'data-sample': entry?.sample ?? '«' + (entry ? entry.labelEt : node.attrs.code) + '»',
        title: entry ? '${' + node.attrs.code + '}' : studioLabel('codeUnknown') + ': ${' + node.attrs.code + '}',
      },
    ]
  },

  renderText({ node }) {
    return '${' + node.attrs.code + '}'
  },
})

/**
 * A whole-paragraph export block (comparison tables, galleries, ESG…).
 * pPr/rPr passthrough keeps the macro paragraph shape for the compiler.
 */
export const TemplateBlock = Node.create({
  name: 'templateBlock',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      code: { default: '' },
      pPr: { default: null, rendered: false },
      rPr: { default: null, rendered: false },
    }
  },

  renderHTML({ node }) {
    const entry = registryEntry(node.attrs.code)
    return [
      'div',
      { class: 'studio-block-card', 'data-code': node.attrs.code },
      ['div', { class: 'studio-block-card-title' }, entry ? entry.labelEt : node.attrs.code],
      ['div', { class: 'studio-block-card-sub' }, studioLabel('blockFilledOnExport')],
    ]
  },

  renderText({ node }) {
    return '${' + node.attrs.code + '}'
  },
})

/**
 * A footnote reference: superscript number in the text flow. The verbatim
 * w:footnoteReference (xml) + run rPr compile back byte-identically; id is
 * a display key into the presentation payload's footnotes map. Numbering
 * is doc-order via CSS counters (Word's auto-renumber semantics) until the
 * page-bottom milestone's JS marker engine takes over. The marker draws in
 * its run's formatting like any run (OoxmlRun): the character style, and the
 * rPr's own properties and switched-off toggles over whatever the paragraph
 * or a table-style region passes down. Word bolds a reference in a bold
 * column, but not one whose run says w:b="0" (the malls' comparables tables).
 */
export const FootnoteRef = Node.create({
  name: 'footnoteRef',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      xml: { default: '', rendered: false },
      rPr: { default: null, rendered: false },
      id: { default: null, rendered: false },
    }
  },

  renderHTML({ node }) {
    const text = node.attrs.id === null ? null : footnoteText(node.attrs.id)
    const { rPr } = node.attrs
    const toggledOff = rPr ? runToggleOffClasses(rPr) : ''
    return [
      'sup',
      {
        class: toggledOff ? `studio-footnote-ref ${toggledOff}` : 'studio-footnote-ref',
        style: (rPr && rPrToCss(rPr)) || undefined,
        'data-r-style': (rPr && rStyleOf(rPr)) || undefined,
        'data-fn-id': node.attrs.id ?? undefined,
        title: text ?? undefined,
      },
    ]
  },

  renderText() {
    return ''
  },
})

/**
 * A lossless invisible block: body-level bookmarkStart/bookmarkEnd riding
 * verbatim between paragraphs. Zero-height (NOT display:none — ProseMirror
 * needs a DOM box for position mapping), unselectable, no card.
 */
export const RawMarker = Node.create({
  name: 'rawMarker',
  group: 'block',
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      xml: { default: '', rendered: false },
    }
  },

  renderHTML() {
    return ['div', { class: 'studio-raw-marker', 'aria-hidden': 'true' }]
  },

  renderText() {
    return ''
  },
})

const RAW_HINTS = ['field', 'table', 'sdt', 'drawing', 'chart', 'hyperlink', 'toc', 'unknown']

/** The chart model a chart rawBlock draws (presentation payload), or null. */
function chartModelOf(node) {
  if (node.attrs.hint !== 'chart') return null
  const reference = chartReference(node.attrs.xml)
  return reference ? (presentationPayload()?.charts?.[reference.rId] ?? null) : null
}

/**
 * A chart rawBlock: the paragraph with its chart drawn in SVG (charts/), or
 * null when it cannot be drawn and the card stays. Drawn with the block's
 * chartEdit override, or with `edit` (a not yet committed one) when given.
 */
function chartSpec(node, edit = undefined) {
  if (node.attrs.hint !== 'chart') return null
  return chartBlockSpec(node.attrs.xml, presentationPayload()?.charts ?? null, studioLabel('rawHint.chart'), edit === undefined ? node.attrs.chartEdit : edit)
}

/** Whether a chart model can be edited on the canvas (RED-317 PR 3d-A). */
function editableModel(model) {
  return Boolean(model && model.version === 1 && !(model.unsupported ?? []).length && model.data && (model.data.series ?? []).length)
}

const fontLoads = new Map()

/**
 * Resolves true once every face the chart draws with is loaded, when one
 * was still loading: the node view then lays the chart out again with the
 * real metrics. Resolves false when nothing had to wait.
 */
function chartFontsLoaded(model) {
  if (typeof document === 'undefined' || !document.fonts?.load) return Promise.resolve(false)
  const pending = []
  for (const style of chartFonts(model)) {
    const font = fontShorthand(style)
    if (document.fonts.check(font)) continue
    if (!fontLoads.has(font)) fontLoads.set(font, document.fonts.load(font).catch(() => []))
    pending.push(fontLoads.get(font))
  }
  return pending.length ? Promise.all(pending).then(() => true) : Promise.resolve(false)
}

/** What a rawBlock shows: a drawn chart, a field's cached result, or the card. */
function rawBlockSpec(node, edit = undefined) {
  const hint = RAW_HINTS.includes(node.attrs.hint) ? node.attrs.hint : 'unknown'

  const chart = chartSpec(node, edit)
  if (chart) return chart

  // Multi-line TOC results carry per-line structure: entry text, trailing
  // page number and TOC level — laid out like Word (level indent, dot
  // leader, right-aligned page) instead of run-together plain text.
  if (hint === 'field' && /^\s*TOC\b/i.test(node.attrs.fieldInstr ?? '') && Array.isArray(node.attrs.fieldLines) && node.attrs.fieldLines.length) {
    return [
      'div',
      {
        class: 'studio-field-text studio-field-toc',
        'data-hint': hint,
        title: node.attrs.fieldInstr ?? undefined,
      },
      ...node.attrs.fieldLines.map((line) => [
        'div',
        {
          class: 'studio-toc-line',
          style: line.level > 1 ? `margin-left: ${(line.level - 1) * 11}pt` : undefined,
        },
        ['span', { class: 'studio-toc-text' }, line.text],
        ...(line.page ? [['span', { class: 'studio-toc-leader' }], ['span', { class: 'studio-toc-page' }, line.page]] : []),
      ]),
    ]
  }

  // Field spans with a cached result render what Word renders (the result
  // text, field-shaded) instead of a locked card — the verbatim xml attr
  // still round-trips untouched.
  if (hint === 'field' && node.attrs.fieldResult) {
    return [
      'div',
      {
        class: 'studio-field-text',
        'data-hint': hint,
        title: node.attrs.fieldInstr ?? undefined,
      },
      node.attrs.fieldResult,
    ]
  }

  // A chart the canvas cannot draw says so on its card (no console noise).
  const unsupported = hint === 'chart' && (chartModelOf(node)?.unsupported ?? []).length > 0
  return [
    'div',
    { class: 'studio-raw-card', 'data-hint': hint, 'data-testid': 'studio-raw-card' },
    ['div', { class: 'studio-raw-card-title' }, studioLabel('rawTitle')],
    ['div', { class: 'studio-raw-card-sub' }, studioLabel('rawHint.' + hint)],
    ...(unsupported ? [['div', { class: 'studio-raw-card-note', 'data-testid': 'studio-chart-unsupported' }, studioLabel('chartUnsupported')]] : []),
    // Shown only while editing (studio.css).
    ['div', { class: 'studio-raw-card-hint' }, studioLabel('rawEditHint')],
  ]
}

/**
 * Opaque Word content preserved verbatim (the parity guarantee). The xml
 * attr is never rendered as such: a chart is drawn from its display model
 * (read-only, RED-317 PR 3c-B), anything else shows a labelled card with
 * the construct hint.
 */
export const RawBlock = Node.create({
  name: 'rawBlock',
  group: 'block',
  atom: true,
  selectable: true,
  // Locked, not fixed: a card can be dragged, moved (moveSelectedBlock) and
  // deleted; its content is edited in Word (Laadi alla Wordina → Laadi Wordist).
  draggable: true,

  addAttributes() {
    return {
      xml: { default: '', rendered: false },
      hint: { default: 'unknown' },
      fieldInstr: { default: null, rendered: false },
      fieldResult: { default: null, rendered: false },
      fieldLines: { default: null, rendered: false },
      // RED-317 PR 3d-A: a chart's data, titles and labels edited on the
      // canvas, as an override of the part it was imported with (contract
      // §7, charts/chartEdit.js); null = untouched.
      chartEdit: { default: null, rendered: false },
    }
  },

  addCommands() {
    return {
      /** One committed chart edit = one transaction, its own undo step. */
      setChartEdit:
        (pos, edit) =>
        ({ state, tr, dispatch }) => {
          const node = state.doc.nodeAt(pos)
          if (!node || node.type.name !== 'rawBlock') return false
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, chartEdit: edit ?? null })
            closeHistory(tr)
          }
          return true
        },
    }
  },

  // Charts re-lay themselves out once their web fonts have loaded, and
  // whenever their override changes; the DOM they redraw is theirs alone
  // (ignoreMutation). An editable chart registers a controller the inline
  // editor drives (charts/chartEditing.js): the editor mounts into a host
  // below the chart and previews uncommitted edits through it.
  addNodeView() {
    return ({ node: initial, getPos, editor }) => {
      let node = initial
      let pending
      const render = () => DOMSerializer.renderSpec(document, rawBlockSpec(node, pending)).dom
      const dom = render()
      const model = chartModelOf(node)
      if (!model || !dom.classList?.contains('studio-chart-block')) return { dom, ignoreMutation: () => true }

      const state = observable({ node, pos: typeof getPos === 'function' ? getPos() : null })
      const listeners = new Set()
      let host = null
      let frame = 0
      const redraw = () => {
        frame = 0
        const started = performance.now()
        const fresh = render()
        const paragraph = fresh.classList?.contains('studio-chart-block') ? fresh.firstElementChild : null
        if (paragraph && dom.firstElementChild) dom.replaceChild(paragraph, dom.firstElementChild)
        performance.measure?.('studio-chart-redraw', { start: started, end: performance.now() })
        for (const listener of listeners) listener()
      }
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(redraw)
      }
      const controller = {
        state,
        model: () => chartModelOf(node),
        pos: () => (typeof getPos === 'function' ? (getPos() ?? null) : null),
        editable: () => editableModel(chartModelOf(node)),
        block: () => dom,
        open: () => {
          if (!host) {
            host = document.createElement('div')
            host.className = 'studio-chart-edit'
            host.setAttribute('contenteditable', 'false')
            dom.appendChild(host)
            dom.classList.add('studio-chart-editing')
            // Selecting text in the sheet must not start dragging the block.
            dom.draggable = false
          }
          return host
        },
        close: () => {
          pending = undefined
          host?.remove()
          host = null
          dom.classList.remove('studio-chart-editing')
          dom.draggable = true
          schedule()
        },
        host: () => host,
        preview: (edit) => {
          pending = edit
          schedule()
        },
        onRender: (listener) => {
          listeners.add(listener)
          return () => listeners.delete(listener)
        },
      }
      registerChartView(dom, controller)

      chartFontsLoaded(model).then((changed) => {
        if (!changed) return
        // The widths measured in the fallback face are stale now.
        forgetChartWidths()
        if (dom.isConnected) redraw()
      })
      // A click on a drawn chart selects it, as a click on a card does:
      // ProseMirror leaves the previous block selected when the click lands
      // on the chart's svg (the block menu would then move or delete that one).
      dom.addEventListener('click', (event) => {
        if (host?.contains(event.target)) return
        const pos = typeof getPos === 'function' ? getPos() : null
        if (typeof pos === 'number' && editor.isEditable && editor.state.selection.from !== pos) editor.commands.setNodeSelection(pos)
      })
      // A double click opens the inline editor on the chart.
      dom.addEventListener('dblclick', (event) => {
        if (host?.contains(event.target)) return
        const pos = typeof getPos === 'function' ? getPos() : null
        if (typeof pos === 'number' && startChartEdit(editor, pos)) event.preventDefault()
      })

      return {
        dom,
        ignoreMutation: () => true,
        // The inline editor's keys, clicks and pastes are its own; while it is
        // open, a press on the chart does not hand the keyboard to the editor
        // (the chart is selected there, and a key would replace it). `Node`
        // here is Tiptap's: the DOM's is globalThis.Node.
        stopEvent: (event) => Boolean(host && event.target instanceof globalThis.Node && (host.contains(event.target) || event.type === 'mousedown')),
        update: (updated) => {
          if (updated.type !== node.type || updated.attrs.hint !== node.attrs.hint) return false
          const changed = updated.attrs.xml !== node.attrs.xml || updated.attrs.chartEdit !== node.attrs.chartEdit
          node = updated
          if (changed) {
            pending = undefined
            schedule()
          }
          state.value = { node, pos: typeof getPos === 'function' ? getPos() : null }
          return true
        },
        destroy: () => {
          if (frame) cancelAnimationFrame(frame)
          unregisterChartView(dom)
          endSessionOf(editor, controller)
        },
      }
    }
  },

  renderHTML({ node }) {
    return rawBlockSpec(node)
  },

  renderText() {
    return ''
  },
})

/** Invisible inline preservation (bookmarks) — a near-zero-width marker. */
export const RawInline = Node.create({
  name: 'rawInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      xml: { default: '', rendered: false },
    }
  },

  renderHTML() {
    return ['span', { class: 'studio-raw-inline', 'aria-hidden': 'true' }]
  },

  renderText() {
    return ''
  },
})

const RAW_RUN_HINTS = ['textbox', 'object', 'drawing']

/** Picture types a browser shows; an OLE preview in EMF/WMF is not one of them. */
const BROWSER_PICTURE = /\.(png|jpe?g|gif)$/i

/**
 * A text box's own paragraphs (the importer maps its w:txbxContent like body
 * content), rendered by this schema's own paragraph, run and mark rendering —
 * the same presentation as the rest of the page. Display only: the box's
 * text changes in Word, never here.
 */
function textBoxContent(node) {
  const content = node.attrs.box?.content
  if (!Array.isArray(content) || !content.length || typeof document === 'undefined') return []
  try {
    const { schema } = node.type
    const fragment = DOMSerializer.fromSchema(schema).serializeFragment(schema.nodeFromJSON({ type: 'doc', content }).content)
    // renderSpec takes elements, not a fragment: the box's blocks one by one.
    return [...fragment.childNodes].filter((child) => child.nodeType === 1)
  } catch {
    return []
  }
}

/** Parsed preview SVGs, one <template> per markup: a re-render clones it instead of parsing again. */
const previewTemplates = new Map()

/** DOMPurify's SVG profile keeps everything the EMF converter writes (shapes, text, clip paths, hatch patterns, data: bitmaps). */
const PREVIEW_PURIFY = { USE_PROFILES: { svg: true, svgFilters: true } }

/**
 * The object's preview SVG in a box of Word's size. Width plus aspect ratio
 * rather than a height, so a box capped at the text width keeps Word's
 * proportions. The markup is the server's (EmfToSvg builds it from numbers
 * and whitelists), and it is still sanitised before it reaches the live DOM:
 * a cloned <script> would run and an event attribute would fire. Null
 * without a preview, outside a browser, or when no <svg> survives.
 */
function previewElement(svg, box, label) {
  if (!svg || typeof document === 'undefined' || !DOMPurify.isSupported) return null
  let template = previewTemplates.get(svg)
  if (!template) {
    template = document.createElement('template')
    template.innerHTML = DOMPurify.sanitize(svg, PREVIEW_PURIFY)
    if (previewTemplates.size >= 64) previewTemplates.clear()
    previewTemplates.set(svg, template)
  }
  if (template.content.firstElementChild?.localName !== 'svg') return null
  const frame = document.createElement('span')
  frame.className = 'studio-object-preview'
  frame.dataset.hint = 'object'
  frame.dataset.testid = 'studio-object-preview'
  frame.setAttribute('role', 'img')
  frame.setAttribute('aria-label', label)
  frame.title = label
  if (box.widthPt > 0 && box.heightPt > 0) {
    frame.style.width = `${box.widthPt}pt`
    frame.style.aspectRatio = `${box.widthPt} / ${box.heightPt}`
  }
  frame.appendChild(template.content.cloneNode(true))
  return frame
}

/**
 * A run holding one Word object — a floating text box or an embedded OLE
 * object (RED-317 PR 3c-A). The whole w:r rides verbatim in xml and compiles
 * back byte for byte, so the paragraph around it stays ordinary editable
 * text. Locked: it can be selected, moved with its text and deleted, never
 * edited inside — its content changes in Word. `box` is the importer's
 * display projection: a text box draws like Word (frame from its anchor, its
 * own paragraphs inside); an object draws its preview picture: the SVG the
 * server converted from Word's EMF preview (PR 3c-C, objectPreview), a
 * picture a browser shows natively, else a labelled frame of the size Word
 * gives it.
 */
/** What a rawRun shows: a text box, an object's preview (or picture, or labelled frame), a drawing's frame. */
function rawRunSpec(node) {
  const hint = RAW_RUN_HINTS.includes(node.attrs.hint) ? node.attrs.hint : 'drawing'
  const box = node.attrs.box ?? {}

  if (hint === 'textbox') {
    const { css, absolute } = textBoxCss(box)
    return [
      'span',
      {
        class: absolute ? 'studio-textbox studio-textbox-abs' : 'studio-textbox',
        style: css || undefined,
        'data-hint': hint,
        'data-testid': 'studio-textbox',
        title: studioLabel('rawRun.textbox'),
      },
      ...textBoxContent(node),
    ]
  }

  const size = [box.widthPt > 0 ? `width: ${box.widthPt}pt` : null, box.heightPt > 0 ? `height: ${box.heightPt}pt` : null].filter(Boolean).join('; ')
  const label = hint === 'object' ? studioLabel(/^Excel\./.test(box.progId ?? '') ? 'rawRun.excel' : 'rawRun.object') : studioLabel('rawRun.drawing')
  const preview = hint === 'object' ? previewElement(objectPreview(box.mediaPart), box, label) : null
  if (preview) {
    return preview
  }
  const picture = hint === 'object' && BROWSER_PICTURE.test(box.mediaPart ?? '') ? mediaUrl(box.mediaPart) : null
  if (picture) {
    return ['img', { src: picture, class: 'studio-object-picture', style: size || undefined, alt: label, title: label, 'data-hint': hint }]
  }

  return [
    'span',
    { class: 'studio-object', style: size || undefined, 'data-hint': hint, 'data-testid': 'studio-object' },
    ['span', { class: 'studio-object-title' }, label],
    ['span', { class: 'studio-object-sub' }, studioLabel(hint === 'object' && box.mediaPart ? 'rawRun.previewUnavailable' : 'rawRun.locked')],
  ]
}

/** The edit model of an Excel object run (RED-317 PR 3d-B), when its own workbook built it; else null. */
function objectSheetOf(node) {
  if (node.attrs.hint !== 'object') return null
  const box = node.attrs.box ?? {}
  const sheet = objectSheet(box.mediaPart)
  return sheet && box.embedPart && sheet.embedPart === box.embedPart ? sheet : null
}

let objectViews = 0

export const RawRun = Node.create({
  name: 'rawRun',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      xml: { default: '', rendered: false },
      hint: { default: 'object', rendered: false },
      box: { default: null, rendered: false },
      // RED-317 PR 3d-B: an Excel object's cells retyped on the canvas
      // (objects/objectEdit.js); null = untouched.
      objectEdit: { default: null, rendered: false },
    }
  },

  addCommands() {
    return {
      /** One committed Excel object edit = one transaction, its own undo step (RED-317 PR 3d-B). */
      setObjectEdit:
        (pos, edit) =>
        ({ state, tr, dispatch }) => {
          const node = state.doc.nodeAt(pos)
          if (!node || node.type.name !== 'rawRun') return false
          if (dispatch) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, objectEdit: edit ?? null })
            closeHistory(tr)
          }
          return true
        },
    }
  },

  // An Excel object with an edit model draws its preview patched with its
  // override (objects/objectPreview.js) and registers a controller the inline
  // editor (StudioObjectEditor) drives, like a chart's (charts/chartEditing.js):
  // the sheet mounts into a host below the preview, uncommitted typing previews
  // live. Anything else renders as it always did.
  addNodeView() {
    return ({ node: initial, getPos, editor }) => {
      let node = initial
      const box = node.attrs.box ?? {}
      const sheet = objectSheetOf(node)
      const svg = sheet ? objectPreview(box.mediaPart) : null
      const label = studioLabel('rawRun.excel')
      let frameElement = svg ? previewElement(svg, box, label) : null
      if (!frameElement) return { dom: DOMSerializer.renderSpec(document, rawRunSpec(node)).dom, ignoreMutation: () => true }

      const prefix = `oe${++objectViews}`
      const dom = document.createElement('span')
      dom.className = 'studio-object-wrap'
      dom.dataset.hint = 'object'
      dom.dataset.testid = 'studio-object-wrap'
      dom.appendChild(frameElement)

      let pending
      let host = null
      let frame = 0
      const listeners = new Set()
      const state = observable({ node, pos: typeof getPos === 'function' ? getPos() : null })
      const draw = () => {
        const started = performance.now()
        const fresh = previewElement(svg, box, label)
        const edit = pending === undefined ? node.attrs.objectEdit : pending
        if (fresh && edit) patchPreview(fresh.querySelector('svg'), sheet, objectFonts(), edit, prefix)
        if (fresh) {
          dom.replaceChild(fresh, frameElement)
          frameElement = fresh
        }
        performance.measure?.('studio-object-redraw', { start: started, end: performance.now() })
        for (const listener of listeners) listener()
      }
      const schedule = () => {
        if (!frame)
          frame = requestAnimationFrame(() => {
            frame = 0
            draw()
          })
      }
      if (node.attrs.objectEdit) draw()

      const controller = {
        kind: 'object',
        state,
        model: () => sheet,
        fonts: () => objectFonts(),
        pos: () => (typeof getPos === 'function' ? (getPos() ?? null) : null),
        editable: () => Boolean(sheet),
        block: () => dom,
        frame: () => frameElement,
        open: () => {
          if (!host) {
            host = document.createElement('div')
            host.className = 'studio-object-edit'
            host.setAttribute('contenteditable', 'false')
            dom.appendChild(host)
            dom.classList.add('studio-object-editing')
          }
          return host
        },
        close: () => {
          pending = undefined
          host?.remove()
          host = null
          dom.classList.remove('studio-object-editing')
          schedule()
        },
        host: () => host,
        preview: (edit) => {
          pending = edit
          schedule()
        },
        onRender: (listener) => {
          listeners.add(listener)
          return () => listeners.delete(listener)
        },
      }
      registerChartView(dom, controller)

      dom.addEventListener('click', (event) => {
        if (host?.contains(event.target)) return
        const pos = typeof getPos === 'function' ? getPos() : null
        if (typeof pos === 'number' && editor.isEditable && editor.state.selection.from !== pos) editor.commands.setNodeSelection(pos)
      })
      dom.addEventListener('dblclick', (event) => {
        if (host?.contains(event.target)) return
        const pos = typeof getPos === 'function' ? getPos() : null
        if (typeof pos === 'number' && startChartEdit(editor, pos)) event.preventDefault()
      })

      return {
        dom,
        ignoreMutation: () => true,
        // While the editor is open its keys, clicks and pastes are its own (see RawBlock).
        stopEvent: (event) => Boolean(host && event.target instanceof globalThis.Node && (host.contains(event.target) || event.type === 'mousedown')),
        update: (updated) => {
          if (updated.type !== node.type || updated.attrs.xml !== node.attrs.xml || updated.attrs.hint !== node.attrs.hint) return false
          const changed = updated.attrs.objectEdit !== node.attrs.objectEdit
          node = updated
          if (changed) {
            pending = undefined
            schedule()
          }
          state.value = { node, pos: typeof getPos === 'function' ? getPos() : null }
          return true
        },
        destroy: () => {
          if (frame) cancelAnimationFrame(frame)
          unregisterChartView(dom)
          endSessionOf(editor, controller)
        },
      }
    }
  },

  renderHTML({ node }) {
    return rawRunSpec(node)
  },

  renderText() {
    return ''
  },
})

/**
 * PAGE / NUMPAGES field projection — emitted by the backend ONLY inside
 * header/footer docs (display-only parts, never compiled). The chrome layer
 * substitutes the real page number per page instance; standalone rendering
 * shows a placeholder glyph.
 */
export const PageField = Node.create({
  name: 'pageField',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      kind: { default: 'page' },
      rPr: { default: null, rendered: false },
    }
  },

  renderHTML({ node }) {
    return ['span', { 'data-page-field': node.attrs.kind, class: 'studio-page-field' }, '#']
  },

  renderText() {
    return '#'
  },
})

/**
 * w:tab — a native inline tab. Interim fixed advance via CSS; the tab-stop
 * engine (M7.6) will size these to the paragraph's real stop positions.
 */
export const Tab = Node.create({
  name: 'tab',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      rPr: { default: null, rendered: false },
    }
  },

  renderHTML() {
    return ['span', { class: 'studio-tab' }]
  },

  renderText() {
    return '\t'
  },
})

/**
 * Explicit page break (w:br type="page") — a compact inline glyph chip.
 * The REAL break is drawn by the pagination layer; this marker only makes
 * the atom visible, selectable and deletable.
 */
export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      rPr: { default: null, rendered: false },
    }
  },

  renderHTML() {
    return ['span', { class: 'studio-page-break', title: studioLabel('pageBreak') }, '⤓']
  },

  renderText() {
    return '\n'
  },
})

/**
 * CSS for a non-wrapped anchor (wrapNone / behindDoc): absolutely
 * positioned inside its anchor paragraph — studio.css makes the paragraph
 * the containing block, so the image scrolls, zooms and paginates with the
 * text it is anchored to.
 *
 * Horizontal is faithful to Word for the corpus-dominant modes; vertical is
 * approximated — page-absolute vertical anchoring cannot survive the
 * spacer-pagination model (one continuous editor flow, page cards drawn
 * underneath), so only paragraph-relative offsets are honored. Documented
 * v1 divergence.
 */
function absAnchorCss(info) {
  const css = []
  const { relativeFrom, offsetPx, align } = info.posH

  if (offsetPx !== null) {
    if (relativeFrom === 'page') {
      // Page-relative x: the anchor paragraph's box starts at the section's
      // left margin, so shift the page offset back into the text column.
      // Null-safe: without a section payload the raw offset is the best
      // available estimate.
      const section = pageSection()
      const marginPx = section ? mmToPx(section.marginLeftMm) : 0
      css.push(`left: ${Math.round(offsetPx - marginPx)}px`)
    } else {
      // margin / column: the paragraph box IS the margin box — the offset
      // applies directly (and is the least-wrong reading for exotic modes).
      css.push(`left: ${offsetPx}px`)
    }
  } else if (align === 'right') {
    css.push('right: 0')
  } else if (align === 'center') {
    css.push('left: 50%', 'transform: translateX(-50%)')
  } else {
    // align left — or nothing usable — pins to the margin edge.
    css.push('left: 0')
  }

  // Vertical: paragraph-relative offsets map 1:1 (the paragraph is the CSS
  // containing block); margin/page-relative verticals pin to the paragraph
  // top instead — see the divergence note above.
  if (info.posV.relativeFrom === 'paragraph' && info.posV.offsetPx !== null) {
    css.push(`top: ${info.posV.offsetPx}px`)
  } else {
    css.push('top: 0')
  }

  return css.join('; ')
}

/**
 * Anchor geometry as a data attribute: the chrome renderer extracts
 * positioned anchors out of header/footer flow and places them absolutely
 * on the page card (layout/anchorPlacement.js) — without re-parsing the
 * drawing xml. Inert in the body canvas.
 */
function anchorData(info) {
  return JSON.stringify({
    kind: info.kind,
    behindDoc: info.behindDoc,
    distPx: info.distPx,
    posH: info.posH,
    posV: info.posV,
  })
}

/**
 * A new picture's templateImage attrs (contract §5) from what the media
 * endpoint answered ({ mediaPart, widthEmu, heightEmu }), or false when it
 * is not one.
 */
export function newPictureAttrs(picture) {
  const mediaPart = picture?.mediaPart
  const widthEmu = Math.round(Number(picture?.widthEmu))
  const heightEmu = Math.round(Number(picture?.heightEmu))
  if (typeof mediaPart !== 'string' || !/^word\/media\/[A-Za-z0-9._-]+$/.test(mediaPart) || !(widthEmu > 0) || !(heightEmu > 0)) return false
  return { drawingXml: null, rPr: null, rId: null, mediaPart, widthEmu, heightEmu, anchored: false, wrapSide: null }
}

/**
 * Inline image. The whole w:drawing travels verbatim in drawingXml; the
 * canvas shows the media part via the studio media endpoint, sized from the
 * EMU extents so layout matches Word.
 */
export const TemplateImage = Node.create({
  name: 'templateImage',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  // A picture the editing UI uploaded and pasted (StudioPaste): its attrs
  // ride as JSON on the transform's marker <img>. Any other <img> is dropped.
  parseHTML() {
    return [
      {
        tag: 'img[data-studio-picture]',
        getAttrs: (element) => {
          try {
            return newPictureAttrs(JSON.parse(element.getAttribute('data-studio-picture')))
          } catch {
            return false
          }
        },
      },
    ]
  },

  addAttributes() {
    return {
      drawingXml: { default: null, rendered: false },
      rPr: { default: null, rendered: false },
      rId: { default: null, rendered: false },
      mediaPart: { default: null, rendered: false },
      widthEmu: { default: null, rendered: false },
      heightEmu: { default: null, rendered: false },
      // wp:anchor projection (body AND header/footer chrome since M8.1):
      // wrapped anchors float left/right like Word, wrapNone/behindDoc
      // position absolutely, the rest center (see the anchored branch).
      anchored: { default: false, rendered: false },
      wrapSide: { default: null, rendered: false },
    }
  },

  renderHTML({ node }) {
    const src = node.attrs.mediaPart ? mediaUrl(node.attrs.mediaPart) : null
    if (!src) {
      return ['span', { class: 'studio-image-missing' }, studioLabel('imageUnavailable')]
    }

    const attrs = { src, class: 'studio-image', loading: 'lazy', decoding: 'async' }
    const width = emuToPx(node.attrs.widthEmu)
    const height = emuToPx(node.attrs.heightEmu)
    if (width) attrs.width = width
    if (height) attrs.height = height

    if (!node.attrs.anchored) {
      const inline = inlinePictureCss(node.attrs.drawingXml, node.attrs.widthEmu, node.attrs.heightEmu)
      if (inline) attrs.style = inline
    } else {
      const info = drawingAnchorInfo(node.attrs.drawingXml)
      const side = node.attrs.wrapSide
      const floats = side === 'left' || side === 'right'

      if (floats && (!info || info.kind === 'square' || info.kind === 'tight')) {
        // Word wraps text around the anchor: a CSS float, with the anchor's
        // text distances as margins on the wrapped sides — a LEFT float
        // pushes text off its right/bottom edges, a RIGHT float off its
        // left/bottom (the outer edges sit against the margin, dist ignored
        // there, exactly like Word).
        const dist = info?.distPx ?? { t: 0, r: 0, b: 0, l: 0 }
        attrs.class += ' studio-image-anchored'
        attrs.style =
          side === 'left'
            ? `float: left; margin: ${dist.t}px ${dist.r}px ${dist.b}px 0`
            : `float: right; margin: ${dist.t}px 0 ${dist.b}px ${dist.l}px`
      } else if (info && (info.kind === 'none' || info.behindDoc)) {
        // No text wrap (or behind-text): absolute positioning inside the
        // anchor paragraph. behindDoc additionally drops below the text —
        // studio.css puts it under the ink but above the page card.
        attrs.class += info.behindDoc ? ' studio-image-abs studio-image-behind' : ' studio-image-abs'
        attrs.style = absAnchorCss(info)
        attrs['data-anchor'] = anchorData(info)
      } else {
        // topAndBottom, wrapSquare bothSides (a full-width banner leaves no
        // room for text beside it) or an unrecognized wrap. Page-relative
        // VERTICAL positions (the MAATUKK page-bottom wave) are absolute
        // page coordinates the editor flow cannot represent — those render
        // as a zero-footprint ghost and the pagination controller projects
        // them onto their page card (layout.bodyAnchors). The rest stay in
        // flow, positioned at Word's x/y when the anchor says where —
        // full-page banners bleed into the margins via margin-left (width
        // capped at the page edge, like Word's clip) and pull up into the
        // top margin via a page-clamped margin-top, while still pushing
        // following content below (Word's below-the-anchor text flow).
        // Without posH data: the centered-block fallback.
        if (info && isPageProjected(info)) {
          attrs.class += ' studio-image-ghost'
          attrs['data-anchor'] = anchorData(info)
          return ['img', attrs]
        }
        attrs.class += ' studio-image-anchored'
        if (info) {
          attrs['data-anchor'] = anchorData(info)
          const flowCss = []
          const marginLeft = flowAnchorMarginLeftPx(info, width, pageSection())
          if (marginLeft !== null) {
            flowCss.push(`margin-left: ${marginLeft}px`, 'max-width: none')
            const cappedWidth = flowAnchorWidthPx(marginLeft, width, pageSection())
            if (cappedWidth !== null) flowCss.push(`width: ${cappedWidth}px`, 'height: auto')
          }
          const marginTop = flowAnchorMarginTopPx(info, pageSection())
          if (marginTop !== null) {
            // The pull-up must NOT margin-collapse through the paragraph —
            // that would shift the paragraph box itself and over-measure
            // the breakable by the pull, desyncing every later page.
            // studio.css makes the paragraph a flow-root for this class.
            flowCss.push(`margin-top: ${marginTop}px`)
            attrs.class += ' studio-image-pulled'
          }
          if (flowCss.length) attrs.style = flowCss.join('; ')
        }
      }
    }

    return ['img', attrs]
  },

  renderText() {
    return ''
  },
})
